import { useMemo, useId, isValidElement } from 'react';
import { css, type Theme } from '@emotion/react';
import { Root as RadixRadioGroup } from '@radix-ui/react-radio-group';
import { WarningCircle } from '@phosphor-icons/react';
import { useEditAppValidation } from 'client/lib/EditApp';
import { Typography } from 'client/components/Typography';
import { RadioGroupItem, type RadioGroupItemProps } from './RadioGroupItem';

export interface RadioGroupProps {
  value: string | null;
  label?: string;
  path?: string;
  empty?: JSX.Element;
  items: (RadioGroupItemProps | JSX.Element)[];
  onChange?: (value: string) => void;
}

export const RadioGroup = ({ value, label, path, empty, items, onChange, ...other }: RadioGroupProps) => {
  const id = useId();
  const children = useMemo(
    () =>
      items.map((item) =>
        isValidElement(item) ? item : <RadioGroupItem key={(item as RadioGroupItemProps).id} {...(item as RadioGroupItemProps)} />
      ),
    [items]
  );

  const validationError = useEditAppValidation(path || null);

  return (
    <>
      {label && (
        <Typography {...({ htmlFor: id } as any)} component="label" css={labelStyle}>
          {label}
          {validationError && (
            <span>
              {' '}
              — <WarningCircle weight="bold" aria-hidden /> {validationError}
            </span>
          )}
        </Typography>
      )}
      {!!empty && !items.length && empty}
      <RadixRadioGroup id={id} value={value || ''} onValueChange={onChange} css={groupStyle} {...other}>
        {children}
      </RadixRadioGroup>
    </>
  );
};

const groupStyle = css`
  display: grid;
  align-self: stretch;
  grid-template-columns: 1fr;
  grid-gap: 2px;
`;

const labelStyle = (theme: Theme) => css`
  display: block;
  align-self: flex-start;
  font-weight: 700 !important;
  font-size: 16px;
  transform-origin: 0 50%;
  transform: translateX(12px) scale(0.75);

  > span {
    color: ${theme.color.red[100]};
    opacity: 0.8;

    svg {
      transform: translateY(3px);
    }
  }
`;
